// ---------------------------------------------------------------------------
// BlockOverlay — block-level hover and selection visual overlays.
//
// Hover: 1px dashed foreground/20 outline (distinct from element blue)
// Selected: 2px solid primary/50 border + block name badge
// ---------------------------------------------------------------------------

import { useEditorStore } from '@/features/page-editor/store/editor-store'

interface BlockOverlayProps {
  blockName: string
  label: string
  isHovered: boolean
}

/**
 * Renders block-level selection badge when selectionTier === 'block'
 * and selectedBlockName matches this block.
 *
 * Hover/selection outlines are handled via CSS data-attributes on the
 * parent wrapper in canvas-viewport.tsx — this component only renders
 * the name badge for selected blocks.
 */
export function BlockOverlay({ blockName, label, isHovered }: BlockOverlayProps) {
  const selectedBlockName = useEditorStore((s) => s.selectedBlockName)
  const selectionTier = useEditorStore((s) => s.selectionTier)

  const isBlockSelected = selectedBlockName === blockName && selectionTier === 'block'

  if (!isBlockSelected) return null

  return (
    <div
      className="absolute -top-5 left-1 z-20 px-1.5 py-0.5 text-[9px] font-medium bg-primary/10 text-primary rounded pointer-events-none"
      data-testid={`block-badge-${blockName}`}
    >
      {label}
    </div>
  )
}
